import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Building2, MessageSquareQuote, Inbox, ArrowRight } from "lucide-react";
import { fetchProducts, fetchClients, fetchTestimonials, fetchContactSubmissions } from "@/lib/api";

export default function AdminOverview() {
  const [counts, setCounts] = useState({ products: 0, clients: 0, testimonials: 0, contacts: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, c, t, s] = await Promise.all([
          fetchProducts(),
          fetchClients(),
          fetchTestimonials(),
          fetchContactSubmissions(),
        ]);
        setCounts({ products: p.length, clients: c.length, testimonials: t.length, contacts: s.length });
        setRecent(s.slice(0, 5));
      } catch {}
    })();
  }, []);

  const tiles = [
    { label: "Products", count: counts.products, icon: Package, to: "/admin/products", tint: "from-blue-500 to-blue-600" },
    { label: "Clients", count: counts.clients, icon: Building2, to: "/admin/clients", tint: "from-emerald-500 to-emerald-600" },
    { label: "Testimonials", count: counts.testimonials, icon: MessageSquareQuote, to: "/admin/testimonials", tint: "from-violet-500 to-violet-600" },
    { label: "Contact Inquiries", count: counts.contacts, icon: Inbox, to: "/admin/contacts", tint: "from-amber-500 to-amber-600" },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-semibold text-slate-900">Welcome back.</h1>
        <p className="text-slate-500 mt-1.5 text-sm">Catalog & inquiry overview for High Roller Consumer Goods Trading.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {tiles.map(({ label, count, icon: Icon, to, tint }) => (
          <Link
            key={label}
            to={to}
            data-testid={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-blue-200 hover:shadow-md transition-all"
          >
            <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${tint} text-white mb-4`}>
              <Icon size={20} />
            </span>
            <div className="font-heading text-3xl font-semibold text-slate-900">{count}</div>
            <div className="text-sm text-slate-500 mt-1 flex items-center gap-1 group-hover:text-blue-600 transition-colors">
              {label} <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </div>
          </Link>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Recent Contact Inquiries</h2>
          <Link to="/admin/contacts" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        {recent.length === 0 ? (
          <p className="text-slate-500 text-sm">No inquiries yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((c) => (
              <li key={c.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate">{c.name} <span className="text-slate-400 font-normal">· {c.subject}</span></div>
                  <div className="text-xs text-slate-500 truncate">{c.email} — {c.message?.slice(0, 60)}{c.message?.length > 60 ? "…" : ""}</div>
                </div>
                <div className="text-xs text-slate-400 shrink-0">{new Date(c.created_at).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
